import {
    createPagePagingActions,
    Field,
    FinalFormInput,
    SortDirection,
    StackSwitchApiContext,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { Grid, IconButton } from "@mui/material";
import { ContentWrapper, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { ResetButton } from "@src/components/ResetButton";
import CountrySelect from "@src/components/select/CountrySelect";
import { StyledFilter, StyledSearch, TableFilter } from "@src/components/table/TableStyles";
import { type GQLPackagesQuery, type GQLPackagesQueryVariables, GQLPackageType } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FC, type PropsWithChildren, useCallback, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { packagesQuery } from "./Table.gql";

interface FilterVariables {
    country?: string;
    query?: string;
    type?: GQLPackageType;
}

interface TableProps {
    persistedStateId?: string;
}
const PackageTable: FC<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const sortApi = useTableQuerySort({ columnName: "package.validTimeInMonths", direction: SortDirection.DESC }, { persistedStateId });
    const filterApi = useTableQueryFilter<FilterVariables>({}, { pagingApi, persistedStateId });
    const stackApi = useContext(StackSwitchApiContext);

    const intl = useIntl();

    const addAdditionalQueryVariablesFn = useCallback(
        () => ({ country: filterApi.current.country, query: filterApi.current.query, type: filterApi.current.type }),
        [filterApi],
    );

    const memoizedVariables = useMemoizedTableVariables<GQLPackagesQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLPackagesQuery, GQLPackagesQueryVariables>()(packagesQuery, {
        resolveTableData: ({ packages }) => ({
            data: packages.nodes,
            totalCount: packages.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, packages),
        }),
        variables: memoizedVariables,
    });

    const packageTypeOptions = [
        {
            label: intl.formatMessage({ id: "reviermanager.packages.filter.packageType.basic", defaultMessage: "Basic" }),
            value: GQLPackageType.Basic,
        },
        {
            label: intl.formatMessage({ id: "reviermanager.packages.filter.packageType.premium", defaultMessage: "Premium" }),
            value: GQLPackageType.Premium,
        },
    ];

    const handleFilterReset = () => {
        filterApi.formApi.reset({});
    };

    const fileName = `${intl.formatMessage({
        id: "reviermanager.common.export.packages",
        defaultMessage: "Pakete",
    })}`;

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    return (
        <ContentWrapper marginVariant="table">
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={1}>
                        <StyledFilter>
                            <StaticSelectField
                                name="country"
                                label={intl.formatMessage({ id: "reviermanager.packages.filter.country", defaultMessage: "Land" })}
                                component={CountrySelect}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={2}>
                        <StyledFilter>
                            <StaticSelectField
                                name="type"
                                label={intl.formatMessage({
                                    id: "reviermanager.packages.filter.packageType",
                                    defaultMessage: "Typ",
                                })}
                                options={packageTypeOptions}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={3}>
                        <StyledFilter>
                            <Field
                                type="text"
                                label={intl.formatMessage({ id: "reviermanager.packages.filter.fulltext", defaultMessage: "Suche" })}
                                name="query"
                                component={FinalFormInput}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.packages.filter.fulltext.placeholder",
                                    defaultMessage: "Suchbegriff",
                                })}
                                startAdornment={<StyledSearch />}
                                variant="vertical"
                                fullWidth
                            />
                        </StyledFilter>
                    </Grid>
                    {Object.keys(filterApi.current).length > 0 && (
                        <Grid>
                            <ResetButton onClick={handleFilterReset} />
                        </Grid>
                    )}
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.packages",
                                defaultMessage: "{count, plural, one {Paket} other {Pakete}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "package.country",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.package.country", defaultMessage: "Land" }),
                                render: ({ country }) => country,
                            },
                            {
                                name: "package.name",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.package.name", defaultMessage: "Paketname" }),
                                render: ({ name }) => name,
                            },
                            {
                                name: "package.description",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.package.description", defaultMessage: "Beschreibung" }),
                                render: ({ description }) => description,
                            },
                            {
                                name: "package.type",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.package.type", defaultMessage: "Typ" }),
                                render: ({ type }) => type,
                            },
                            {
                                name: "package.validTimeInMonths",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.package.table.validTimeInMonths", defaultMessage: "gültig für" }),
                                render: ({ validTimeInMonths }) => (
                                    <>
                                        {`${validTimeInMonths} `}
                                        <FormattedMessage
                                            id="reviermanager.package.months"
                                            defaultMessage="{validTimeInMonths, plural, one {Monat} other {Monate}}"
                                            values={{
                                                validTimeInMonths,
                                            }}
                                        />
                                    </>
                                ),
                                renderExcel: ({ validTimeInMonths }) =>
                                    `${validTimeInMonths} ${intl.formatMessage(
                                        {
                                            id: "reviermanager.package.months",
                                            defaultMessage: "{validTimeInMonths, plural, one {Monat} other {Monate}}",
                                        },
                                        { validTimeInMonths },
                                    )}`,
                            },
                            {
                                name: "actions",
                                visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                render: ({ id }) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            stackApi.activatePage("edit", String(id));
                                        }}
                                    >
                                        <ArrowRight color="primary" />
                                    </IconButton>
                                ),
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && <CSVExportButton exportApi={exportApi} />}
        </ContentWrapper>
    );
};

export default PackageTable;
